import _ from 'lodash';
import {
  IReportVideo,
  IReportVideoMetaData,
  IReportMeasurement,
  IReportMeasurementGroup,
  IMeasurement,
  IReportPerson,
  IMultiGroupReport,
  IStroke,
  IVideo,
  IOptimization,
  IReportOptimization,
  ITrajectoryCoordinate,
  IMeasurementReport,
  IMultiGroupReportConfiguration,
  ITestResult,
  ISponsorReportInfo,
  IFacilityReportInfo,
  IReportEnvironment,
  ReportKind,
  GetReportKind,
  ClubTypeAbbreviationDictionary,
} from 'Data';
import { IReportDataConversionService } from 'Services';
import { TargetsHelper } from 'MeasurementsCalculations';

export class ReportDataConversionService implements IReportDataConversionService {
  convertOptimizedData(data: IOptimization): IReportOptimization {
    const optimization: IReportOptimization = {
      carry: {
        value: data.Carry && data.Carry.Value,
        lower: data.Carry && data.Carry.Lower,
        upper: data.Carry && data.Carry.Upper,
      },
      total: {
        value: data.Total && data.Total.Value,
        lower: data.Total && data.Total.Lower,
        upper: data.Total && data.Total.Upper,
      },
      attackAngle: {
        value: data.AttackAngle && data.AttackAngle.Value,
        lower: data.AttackAngle && data.AttackAngle.Lower,
        upper: data.AttackAngle && data.AttackAngle.Upper,
      },
      spinLoft: {
        value: data.SpinLoft && data.SpinLoft.Value,
        lower: data.SpinLoft && data.SpinLoft.Lower,
        upper: data.SpinLoft && data.SpinLoft.Upper,
      },
      ballSpeed: {
        value: data.BallSpeed && data.BallSpeed.Value,
        lower: data.BallSpeed && data.BallSpeed.Lower,
        upper: data.BallSpeed && data.BallSpeed.Upper,
      },
      launchAngle: {
        value: data.LaunchAngle && data.LaunchAngle.Value,
        lower: data.LaunchAngle && data.LaunchAngle.Lower,
        upper: data.LaunchAngle && data.LaunchAngle.Upper,
      },
      spinRate: {
        value: data.SpinRate && data.SpinRate.Value,
        lower: data.SpinRate && data.SpinRate.Lower,
        upper: data.SpinRate && data.SpinRate.Upper,
      },
      height: { value: data.Height.Value, lower: data.Height.Lower, upper: data.Height.Upper },
      trajectory: data.Trajectory.map(value => {
        const coor: ITrajectoryCoordinate = { x: value.X, y: value.Y, z: value.Z };
        return coor;
      }),
      lowerTrajectory: data.LowerTrajectory.map(value => {
        const coor: ITrajectoryCoordinate = { x: value.X, y: value.Y, z: value.Z };
        return coor;
      }),
      upperTrajectory: data.UpperTrajectory.map(value => {
        const coor: ITrajectoryCoordinate = { x: value.X, y: value.Y, z: value.Z };
        return coor;
      }),
      optimalCarry: data.OptimalCarry,
      optimalTotal: data.OptimalTotal,
    };
    return optimization;
  }

  convertData(report: IMultiGroupReport): IMeasurementReport {
    const groups = new Array<IReportMeasurementGroup>();
    const sponsors = new Array<ISponsorReportInfo>();
    const facilities = new Array<IFacilityReportInfo>();
    const strokeGroups = report.StrokeGroups;
    const id = report.Id;
    //Changing the drShotAnalysis report kind to use multiGroupReport
    //if (ReportKind[report.Kind] === "drShotAnalysis") {
    //    report.Kind = "multiGroupReport";
    //}

    // Convert the names of tiles. TPS/iOS does not agree on tile names.
    if (report && report.Settings && report.Settings.Tiles && report.Settings.Tiles.length) {
      for (let i = 0; i < report.Settings.Tiles.length; i++) {
        const currentReportKind = GetReportKind(report.Kind);
        if (
          currentReportKind === ReportKind.puttingCompareReport ||
          currentReportKind === ReportKind.puttingMultiGroupReport ||
          currentReportKind === ReportKind.puttingSingleGroupReport ||
          currentReportKind === ReportKind.puttingSingleShotReport
        ) {
          if (report.Settings.Tiles[i] === 'TrackedDistance') {
            report.Settings.Tiles[i] = 'LastData';
          }
          if (report.Settings.Tiles[i] === 'SideDistance') {
            report.Settings.Tiles[i] = 'Side';
          }
          if (report.Settings.Tiles[i] === 'Distance') {
            report.Settings.Tiles[i] = 'Total';
          }
        } else {
          if (report.Settings.Tiles[i] === 'MaxHeight') {
            report.Settings.Tiles[i] = 'Height';
          }
          if (report.Settings.Tiles[i] === 'CarrySide') {
            report.Settings.Tiles[i] = 'Side';
          }
          if (report.Settings.Tiles[i] === 'TotalSide') {
            report.Settings.Tiles[i] = 'SideTotal';
          }
          if (report.Settings.Tiles[i] === 'DistanceFromPin') {
            report.Settings.Tiles[i] = 'DistanceToPin';
          }
          if (report.Settings.Tiles[i] === 'LengthToTarget') {
            report.Settings.Tiles[i] = 'DistanceToTarget';
          }
        }
      }
    }

    if (strokeGroups) {
      for (let i = 0; i < strokeGroups.length; i++) {
        const dateString = strokeGroups[i].Player.Birthday ?? '';
        const person: IReportPerson = {
          id: strokeGroups[i].Player.Id ? strokeGroups[i].Player.Id.toLowerCase() : '',
          email: strokeGroups[i].Player.Email ?? '',
          name: strokeGroups[i].Player.Name ?? '',
          image: strokeGroups[i].Player.Image ?? '',
          handicap: strokeGroups[i].Player.Hcp ?? '',
          birthday: new Date(dateString),
        };
        person.age = this.calculateAge(person.birthday, new Date());

        const reportMeasurementGroup: IReportMeasurementGroup = {
          inYards: false,
          targetDefinition: strokeGroups[i].Target ?? '',
          target: strokeGroups[i].Target ?? '',
          lengthToTarget: strokeGroups[i].LengthToTarget,
          averageScore: strokeGroups[i].AvgScore,
          averageDistanceFromPin: strokeGroups[i].AvgDistanceFromPin,
          clubType: strokeGroups[i].Club ?? '',
          abbreviatedClubType: ClubTypeAbbreviationDictionary[strokeGroups[i].Club ?? ''],
          ball: strokeGroups[i].Ball ?? '',
          tags: strokeGroups[i].Tags ?? [],
          date: strokeGroups[i].Date ?? '',
          player: person,
          title: strokeGroups[i].Title ?? '',
          normalStrokes: new Array<IReportMeasurement>(),
          normalizedStrokes: new Array<IReportMeasurement>(),
          id: strokeGroups[i].Id ? strokeGroups[i].Id.toLowerCase() : '',
        };
        for (let j = 0; j < strokeGroups[i].Strokes.length; j++) {
          const reportMeasurement = this.getReportMeasurement(strokeGroups[i].Strokes[j]);
          if (reportMeasurement) {
            reportMeasurementGroup.normalStrokes.push(reportMeasurement);
          }
          const currentReportKind = GetReportKind(report.Kind);
          if (currentReportKind !== ReportKind.combineTestReport) {
            const reportNormalizedMeasurement = this.getReportNormalizedMeasurement(
              strokeGroups[i].Strokes[j]
            );
            if (reportNormalizedMeasurement) {
              reportMeasurementGroup.normalizedStrokes.push(reportNormalizedMeasurement);
            }
          }
        }

        if (report.TestResult) {
          if (report.TestResult.Definition) {
            if (report.TestResult.Definition.InYards) {
              reportMeasurementGroup.inYards = report.TestResult.Definition.InYards;
            }
          }
        }
        groups.push(reportMeasurementGroup);
      }
    }

    const reportSponsors = report.Sponsors;
    if (reportSponsors) {
      for (let i = 0; i < reportSponsors.length; i++) {
        const sponsor = reportSponsors[i];
        const reportSponsor: ISponsorReportInfo = {
          headline: sponsor.Name ?? '',
          logo: sponsor.LogoUri ?? '',
          link: sponsor.Link ? this.fixLinkProtocol(sponsor.Link) : '',
          description: sponsor.Description ?? '',
          kind: sponsor.Kind ?? '',
          id: sponsor.Id ?? '',
        };
        sponsors.push(reportSponsor);
      }
    }

    const normalizedReportEnvironment: IReportEnvironment = { altitude: 0, temperature: 25 };
    const normalizedEnvironment = report.Environment;
    if (normalizedEnvironment) {
      if (normalizedEnvironment.Altitude) {
        normalizedReportEnvironment.altitude = report.Environment.Altitude;
      }

      if (report.Environment.Temperature) {
        // the temperature in the environment is always Celsius
        normalizedReportEnvironment.temperature = report.Environment.Temperature;
      }
    }

    const facility = report.Facility;
    if (facility) {
      const reportFacility: IFacilityReportInfo = {
        headline: facility.Name ?? '',
        logo: facility.Logo ?? '',
        link: facility.Link ? this.fixLinkProtocol(facility.Link) : '',
        description: facility.Description ?? '',
        kind: '',
      };
      facilities.push(reportFacility);
    }

    const instructor: IReportPerson = {
      id: report.User.Id.toLowerCase(),
      email: report.User.Email,
      name: report.User.Name,
      image: report.User.Image,
    };
    const drivingRangeSettings = report.Settings.DrivingRangeSettings;
    let siteExists = false;
    if (drivingRangeSettings) {
      siteExists = report.Settings.DrivingRangeSettings ? true : false;
    }

    const settings: IMultiGroupReportConfiguration = {
      tiles: report.Settings.Tiles ? report.Settings.Tiles : [],
      units: report.Settings.UnitSystem ? report.Settings.UnitSystem : null,
      instructorMessage: report.Settings.InstructorMessage
        ? report.Settings.InstructorMessage
        : null,
      isTps: report.Settings.Client === 'tps',
      isIos: report.Settings.Client === 'ios',
      //Default to web
      isWeb: report.Settings.Client !== undefined ? report.Settings.Client === 'web' : true,
      clubDataOn: report.Settings.ClubDataOn !== undefined ? report.Settings.ClubDataOn : null,
      summaryOn: report.Settings.SummaryOn !== undefined ? report.Settings.SummaryOn : null,
      dispersionOn:
        report.Settings.DispersionOn !== undefined ? report.Settings.DispersionOn : null,
      optimizerOn: report.Settings.OptimizerOn !== undefined ? report.Settings.OptimizerOn : null,
      skidAndRollOn:
        report.Settings.SkidAndRollOn !== undefined ? report.Settings.SkidAndRollOn : null,
      reportSelection: report.Settings.ReportSelection ? report.Settings.ReportSelection : '',
      trajectoryOn:
        report.Settings.TrajectoryOn !== undefined ? report.Settings.TrajectoryOn : null,
      videoOn: report.Settings.VideoOn !== undefined ? report.Settings.VideoOn : null,
      selectedDispersionMode: report.Settings.SelectedDispersionMode
        ? report.Settings.SelectedDispersionMode
        : null,
      showNormalizedData: report.Settings.ShowNormalizedData
        ? report.Settings.ShowNormalizedData
        : this.getDefaultShowNormalizedData(GetReportKind(report.Kind)),
      selectedOptimizedType: report.Settings.SelectedOptimizedType
        ? report.Settings.SelectedOptimizedType
        : null,
      selectedOptimizerView: report.Settings.SelectedOptimizerView
        ? report.Settings.SelectedOptimizerView
        : null,
      selectedView: report.Settings.SelectedView ? report.Settings.SelectedView : null,
      selectedStroke: report.Settings.SelectedStroke ? report.Settings.SelectedStroke : null,
      selectedStrokesGroups: report.Settings.SelectedStrokesGroups
        ? report.Settings.SelectedStrokesGroups
        : [],
      selectedGroupOfStrokes: report.Settings.SelectedGroupOfStrokes
        ? report.Settings.SelectedGroupOfStrokes
        : null,
      selectedClubType: report.Settings.SelectedClubType ? report.Settings.SelectedClubType : null,
      hiddenShots: report.Settings.HiddenShots ?? [],
      selectedVideoAngles: report.Settings.SelectedVideoAngles ?? [],
      showBottomCommercial: report.Settings.ShowBottomCommercial
        ? report.Settings.ShowBottomCommercial
        : null,
      drivingRangeSettings: {
        id: (siteExists && report.Settings.DrivingRangeSettings.Site._id) ?? null,
        siteName: (siteExists && report.Settings.DrivingRangeSettings.Site.Name) ?? null,
        siteShortName:
          siteExists && typeof report.Settings.DrivingRangeSettings.Site.ShortName
            ? report.Settings.DrivingRangeSettings.Site.ShortName
            : null,
      },
    };
    const testResultExists = report.TestResult !== undefined && report.TestResult !== null;
    let definitionExists = false;
    let drivingStatisticsExists = false;
    if (testResultExists) {
      drivingStatisticsExists =
        report.TestResult.DrivingStatistics !== undefined &&
        report.TestResult.DrivingStatistics !== null;
      definitionExists =
        report.TestResult.Definition !== undefined && report.TestResult.Definition !== null;
    }
    const testResult: ITestResult = {
      definition: {
        inYards: (definitionExists && report.TestResult.Definition.InYards) ?? null,
        indoorTest: (definitionExists && report.TestResult.Definition.IndoorTest) ?? null,
        location: (definitionExists && report.TestResult.Definition.Location) ?? null,
        temperature: (definitionExists && report.TestResult.Definition.Temperature) ?? 25,
        wind: (definitionExists && report.TestResult.Definition.Wind) ?? null,
      },
      statistics: {
        avgScore: (testResultExists && report.TestResult.Statistics.AvgScore) ?? null,
        numberOfDeletedStrokes:
          (testResultExists && report.TestResult.Statistics?.NumberOfDeletedStrokes) ?? 0,
      },
      drivingStatistics: {
        maxClubSpeed: drivingStatisticsExists
          ? report.TestResult.DrivingStatistics.MaxClubSpeed
          : null,
      },
    };
    // The backend might return temperature in Kelvins or Celsius.
    if (testResult.definition.temperature && testResult.definition.temperature > 100)
      testResult.definition.temperature -= 273.15;

    //Sorting the groups according to the length to target in the combine report and test center reports.
    const sortedGroups =
      report.Kind === ReportKind.combineTestReport || report.Kind === ReportKind.testCenterReport
        ? _.sortBy(groups, [
            function(group) {
              return TargetsHelper.findTargetValuesForSorting(group.target ?? '');
            },
          ])
        : groups;
    const result: IMeasurementReport = {
      groups: groups ? sortedGroups : null,
      sponsors: sponsors ? sponsors : null,
      facilities: facilities ? facilities : null,
      kind: report.Kind ? report.Kind : '',
      tiles: report.Tiles ? report.Tiles : [],
      settings: settings ? settings : null,
      testResult: testResult,
      time: report.Time ? new Date(report.Time) : null,
      user: instructor,
      environment: normalizedReportEnvironment,
      id: id,
    };
    return result;
  }

  getDefaultShowNormalizedData(reportKind: ReportKind): boolean {
    switch (reportKind) {
      case ReportKind.multiGroupReport:
      case ReportKind.singleGroupReport:
      case ReportKind.singleShotReport:
      case ReportKind.compareReport:
        return true;

      default:
        return false;
    }
  }

  getReportNormalizedMeasurement(stroke: IStroke): IReportMeasurement | undefined {
    const measurement = stroke.NormalizedMeasurement;
    if (measurement) {
      return this.getMeasurement(stroke, measurement);
    }
    return undefined;
  }

  getReportMeasurement(stroke: IStroke): IReportMeasurement | undefined {
    const measurement = stroke.Measurement;
    if (measurement) {
      return this.getMeasurement(stroke, measurement);
    }
    return undefined;
  }

  //unit conversion should also happen here
  getMeasurement(stroke: IStroke, measurement: IMeasurement): IReportMeasurement {
    const result: IReportMeasurement = {
      id: measurement?.Id ? measurement.Id.toLowerCase() : '',
      time: measurement && measurement.Time ? new Date(stroke.Time) : new Date(),
      tags: stroke?.Tags ?? [],
      club: stroke?.Club ?? '',
      ball: stroke?.Ball ?? '',
      optimizeOn: stroke?.OptimizeOn ?? false,
      uploadOn: stroke?.UploadOn ?? false,
      videos: stroke?.Videos ? this.convertVideo(stroke.Videos) : [],
      playerDexterity: measurement && measurement.PlayerDexterity,
      attackAngle: measurement && measurement.AttackAngle,
      launchDirection: measurement && measurement.LaunchDirection,
      ballSpeed: measurement && measurement.BallSpeed,
      clubPath: measurement && measurement.ClubPath,
      clubSpeed: measurement && measurement.ClubSpeed,
      dynamicLoft: measurement && measurement.DynamicLoft,
      faceAngle: measurement && measurement.FaceAngle,
      faceToPath: measurement && measurement.FaceToPath,
      launchAngle: measurement && measurement.LaunchAngle,
      smashFactor: measurement && measurement.SmashFactor,
      spinAxis: measurement && measurement.SpinAxis,
      spinLoft: measurement && measurement.SpinLoft,
      spinRate: measurement && measurement.SpinRate,
      spinIndex: measurement && measurement.SpinIndex,
      smashIndex: measurement && measurement.SmashIndex,
      ballSpeedDifference: measurement && measurement.BallSpeedDifference,
      spinRateDifference: measurement && measurement.SpinRateDifference,
      swingDirection: measurement && measurement.SwingDirection,
      swingPlane: measurement && measurement.SwingPlane,
      swingRadius: measurement && measurement.SwingRadius,
      dPlaneTilt: measurement && measurement.DPlaneTilt,
      height: measurement && measurement.MaxHeight,
      carry: measurement && measurement.Carry,
      total: measurement && measurement.Total,
      //side can mean different things in tps, in putting reports side refers to value called Side Distance
      side:
        measurement && measurement.CarrySide
          ? measurement.CarrySide
          : measurement && measurement.Side,
      sideTotal: measurement && measurement.TotalSide,
      landingAngle: measurement && measurement.LandingAngle,
      hangTime: measurement && measurement.HangTime,
      lastData: measurement && measurement.LastData,
      lowPointDistance: measurement && measurement.LowPointDistance,
      //It seems like tps returns DistanceToPin and mytrackman.com returns distanceFromPin
      distanceToPin:
        measurement && measurement.DistanceToPin
          ? measurement.DistanceToPin
          : measurement && measurement.DistanceFromPin,
      distanceToTarget: measurement && measurement.LengthToTarget,
      score: measurement && measurement.Score,
      distanceScore: measurement && measurement.DistanceScore,
      directionScore: measurement && measurement.DirectionScore,
      effectiveStimp: measurement && measurement.EffectiveStimp,
      speedDrop: measurement && measurement.SpeedDrop,
      rollPercentage: measurement && measurement.RollPercentage,
      //rollDeceleration: measurement.RollDeceleration ?? null,
      rollSpeed: measurement && measurement.RollSpeed,
      skidDistance: measurement && measurement.SkidDistance,
      totalBreak: measurement && measurement.TotalBreak,
      //Impact location tiles
      impactHeight: measurement && measurement.ImpactHeight,
      impactOffset: measurement && measurement.ImpactOffset,

      //Additional parameters added to the TPS
      backswingTime: measurement && measurement.BackswingTime,
      forwardswingTime: measurement && measurement.ForwardswingTime,
      tempo: measurement && measurement.Tempo,
      strokeLength: measurement && measurement.StrokeLength,
      dynamicLie: measurement && measurement.DynamicLie,
      curve: measurement && measurement.Curve,

      trajectory:
        measurement && measurement.BallTrajectory
          ? measurement.BallTrajectory.map(value => {
              const coor: ITrajectoryCoordinate = { x: value.X, y: value.Y, z: value.Z };
              return coor;
            })
          : [],
    };
    return result;
  }

  convertVideo(videos: Array<IVideo>): Array<IReportVideo> {
    const result = new Array<IReportVideo>();
    videos.forEach((video: IVideo) => {
      const uri = video.Uri ? video.Uri : '';
      const poster = video.Poster ? video.Poster : '';
      let metaData: IReportVideoMetaData;
      if (video.Metadata) {
        metaData = {
          framesPerSecond: video.Metadata.FramesPrSecond,
          speedFactor: video.Metadata.SpeedFactor,
          duration: video.Metadata.Duration,
          height: video.Metadata.Height,
          width: video.Metadata.Width,
        };
      } else {
        metaData = {
          framesPerSecond: undefined,
          speedFactor: undefined,
          duration: undefined,
          height: undefined,
          width: undefined,
        };
      }

      const reportVideo: IReportVideo = {
        id: video.Id ? video.Id.toLowerCase() : '',
        url: uri ? uri : '',
        posterUrl: poster ? poster : '',
        angle: video.Angle ? video.Angle : '',
        metaData: metaData,
      };

      result.push(reportVideo);
    });

    return result;
  }

  fixLinkProtocol(link: string) {
    if (
      link.lastIndexOf('https', 0) === 0 ||
      link.lastIndexOf('http', 0) === 0 ||
      link.lastIndexOf('//', 0) === 0
    )
      return link;

    return 'http://' + link;
  }

  calculateAge(birthDate?: Date, otherDate?: Date): number | undefined {
    if (!birthDate || !otherDate) {
      return undefined;
    }
    let years = otherDate.getFullYear() - birthDate.getFullYear();
    if (
      otherDate.getMonth() < birthDate.getMonth() ||
      (otherDate.getMonth() === birthDate.getMonth() && otherDate.getDate() < birthDate.getDate())
    ) {
      years--;
    }

    return years;
  }
}
